/**
 * /app/admin/assignments — матрица назначений стратегий на аккаунты
 * (PULSE-230 фаза 1, admin-only).
 *
 * Строки — стратегии (лаба и дискавери), колонки — торговые аккаунты,
 * ячейки — тумблеры trading_user_strategies. Включение на РЕАЛ-аккаунт
 * требует подтверждения. Стратегии в shadow-gate помечаются: линк
 * запишется, но триггер держит его выключенным до прохождения гейта.
 */
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Store } from 'lucide-react'
import { api } from '@/lib/api'
import { cn } from '@/lib/cn'

type Filter = 'lab' | 'discovery' | 'all'

const FILTERS: Array<{ key: Filter; label: string }> = [
  { key: 'lab', label: 'Лаборатория' },
  { key: 'discovery', label: 'Дискавери' },
  { key: 'all', label: 'Все' },
]

const ACCOUNT_NAMES: Record<number, string> = {
  1: 'Дискавери',
  2: 'Реал',
  7: 'Зеркало',
  8: 'Лаборатория',
  12: 'Внешний',
}

function useMatrix() {
  return useQuery({
    queryKey: ['admin', 'assignments'],
    queryFn: async () => (await api.get<any>('/admin/assignments')).data,
    staleTime: 30_000,
    refetchInterval: 60_000,
  })
}

function Switch({
  on, busy, onToggle,
}: { on: boolean; busy?: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={busy}
      aria-pressed={on}
      className={cn(
        'relative w-[30px] h-[17px] rounded-full border transition-colors align-middle',
        on ? 'bg-pulse-faint border-pulse' : 'bg-elevated border-line hover:border-lineHi',
        busy && 'opacity-50 cursor-wait',
      )}
    >
      <span className={cn('absolute top-[2px] w-[11px] h-[11px] rounded-full transition-all',
        on ? 'right-[2px] bg-pulse' : 'left-[2px] bg-textFaint')} />
    </button>
  )
}

export default function Assignments() {
  const [filter, setFilter] = useState<Filter>('lab')
  const [onlyLinked, setOnlyLinked] = useState(false)
  const [q, setQ] = useState('')
  const [pending, setPending] = useState<string | null>(null)
  const qc = useQueryClient()
  const { data, isLoading } = useMatrix()

  const publish = useMutation({
    mutationFn: async (v: { strategy_id: number; visible: boolean }) =>
      (await api.post<any>('/admin/assignments/publish', v)).data,
    onSettled: () => qc.invalidateQueries({ queryKey: ['admin', 'assignments'] }),
  })

  const toggle = useMutation({
    mutationFn: async (v: { strategy_id: number; user_id: number; active: boolean }) =>
      (await api.post<any>('/admin/assignments/toggle', v)).data,
    onMutate: (v) => setPending(`${v.strategy_id}:${v.user_id}`),
    onSuccess: (resp) => {
      if (resp.shadow_gated) {
        window.alert('Стратегия в shadow-gate: назначение записано, но включится только после прохождения гейта.')
      }
    },
    onSettled: () => {
      setPending(null)
      qc.invalidateQueries({ queryKey: ['admin', 'assignments'] })
    },
  })

  const accounts = (data?.accounts ?? []) as any[]
  const strategies = useMemo(() => {
    let rows = (data?.strategies ?? []) as any[]
    if (filter === 'lab') rows = rows.filter((s) => s.managed_by !== 'discovery')
    if (filter === 'discovery') rows = rows.filter((s) => s.managed_by === 'discovery')
    if (onlyLinked) rows = rows.filter((s) => Object.values(s.links ?? {}).some(Boolean))
    if (q.trim()) {
      const needle = q.trim().toLowerCase()
      rows = rows.filter((s) => s.name.toLowerCase().includes(needle))
    }
    return rows
  }, [data, filter, onlyLinked, q])

  const onToggle = (s: any, uid: number, next: boolean) => {
    const acct = accounts.find((a) => a.id === uid)
    if (next && acct && !acct.demo) {
      const name = ACCOUNT_NAMES[uid] ?? acct.label
      if (!window.confirm(`Включить «${s.name}» на РЕАЛ-аккаунте «${name}»? Стратегия начнёт торговать реальными деньгами.`)) {
        return
      }
    }
    toggle.mutate({ strategy_id: s.id, user_id: uid, active: next })
  }

  return (
    <div className="p-4 sm:p-6 space-y-5 max-w-[1280px]">
      <header className="flex items-center gap-4 flex-wrap">
        <h1 className="text-xl font-semibold tracking-tight">Назначения</h1>

        <div className="flex bg-surface border border-line rounded-md p-0.5 gap-0.5">
          {FILTERS.map((f) => (
            <button key={f.key}
              onClick={() => setFilter(f.key)}
              className={cn('px-3 py-1.5 text-xs font-medium rounded-sm transition-colors',
                filter === f.key
                  ? 'bg-elevated text-text shadow-soft'
                  : 'text-textFaint hover:text-textDim')}
            >{f.label}</button>
          ))}
        </div>

        <label className="flex items-center gap-2 text-xs text-textDim cursor-pointer select-none">
          <input type="checkbox" className="sr-only" checked={onlyLinked}
            onChange={(e) => setOnlyLinked(e.target.checked)} />
          <span className={cn('relative w-[30px] h-[17px] rounded-full border transition-colors flex-none',
            onlyLinked ? 'bg-pulse-faint border-pulse' : 'bg-elevated border-line')}>
            <span className={cn('absolute top-[2px] w-[11px] h-[11px] rounded-full transition-all',
              onlyLinked ? 'right-[2px] bg-pulse' : 'left-[2px] bg-textFaint')} />
          </span>
          только с назначениями
        </label>

        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="поиск по имени…"
          className="ml-auto bg-surface border border-line rounded-md px-3 py-1.5 text-xs w-52
                     placeholder:text-textFaint focus:border-lineHi outline-none"
        />
        {isLoading && <span className="text-xs text-textFaint">загрузка…</span>}
      </header>

      <div className="bg-surface border border-line rounded-lg shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line">
                <th className="px-4 py-2.5 font-medium text-2xs tracking-wide text-textFaint text-left">
                  Стратегия <span className="font-mono">· {strategies.length}</span>
                </th>
                {accounts.map((a) => (
                  <th key={a.id} className={cn('px-3 py-2.5 text-center whitespace-nowrap',
                    !a.is_active && 'opacity-45')}>
                    <span className="block text-xs font-semibold text-textDim">
                      {ACCOUNT_NAMES[a.id] ?? a.label}
                    </span>
                    <span className="inline-flex gap-1 mt-1">
                      <span className={cn('pill font-sans uppercase tracking-wider text-[9.5px]',
                        a.demo ? 'bg-elevated text-textFaint' : 'bg-rose-faint text-rose')}>
                        {a.demo ? 'демо' : 'реал'}
                      </span>
                      {!a.is_active && (
                        <span className="pill font-sans uppercase tracking-wider text-[9.5px] bg-elevated text-textFaint">выкл</span>
                      )}
                      {a.is_active && !a.has_keys && (
                        <span className="pill font-sans uppercase tracking-wider text-[9.5px] bg-amber-faint text-amber">нет ключей</span>
                      )}
                    </span>
                    <span className="block text-2xs text-textFaint font-mono mt-0.5">
                      {a.active_links} вкл
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {strategies.map((s: any) => {
                const gated = s.shadow_gate_state === 'pending'
                return (
                  <tr key={s.id} className="border-b border-line/50 last:border-b-0 transition-colors hover:bg-elevated/60">
                    <td className="px-4 py-2">
                      <span className="flex items-center gap-2 font-semibold whitespace-nowrap max-w-[340px] overflow-hidden text-ellipsis">
                        <Link to={`/app/lab/${s.id}`} className="hover:text-fire transition-colors">
                          {s.name}
                        </Link>
                        <span className={cn('pill text-[9.5px] font-bold',
                          s.dir === 'long' ? 'bg-pulse-faint text-pulse' : 'bg-rose-faint text-rose')}>
                          {s.dir === 'long' ? 'LONG' : 'SHORT'}
                        </span>
                        {s.managed_by !== 'discovery' && (
                          <span className="pill font-sans text-[9.5px] uppercase tracking-wider bg-sky-faint text-sky">lab</span>
                        )}
                        {gated && (
                          <span className="pill font-sans text-[9.5px] uppercase tracking-wider bg-amber-faint text-amber">gate</span>
                        )}
                        {!s.enabled && (
                          <span className="pill font-sans text-[9.5px] uppercase tracking-wider bg-elevated text-textFaint">выкл</span>
                        )}
                        <button
                          onClick={() => publish.mutate({ strategy_id: s.id, visible: !s.catalog_visible })}
                          title={s.catalog_visible
                            ? 'опубликована в каталоге — клик, чтобы скрыть'
                            : 'скрыта из каталога — клик, чтобы опубликовать юзерам'}
                          className={cn('inline-flex items-center transition-colors',
                            s.catalog_visible ? 'text-fire' : 'text-textFaint/50 hover:text-textDim')}
                        >
                          <Store size={13} />
                        </button>
                      </span>
                      <span className="flex gap-2.5 mt-0.5 text-2xs text-textFaint font-mono">
                        <span>7д: {s.n_7d} сд.</span>
                        {s.pp_7d != null && (
                          <span className={Number(s.pp_7d) >= 0 ? 'text-pulse' : 'text-rose'}>
                            {Number(s.pp_7d) > 0 ? '+' : ''}{s.pp_7d} pp
                          </span>
                        )}
                      </span>
                    </td>
                    {accounts.map((a) => {
                      const on = s.links?.[String(a.id)] === true
                      const busy = pending === `${s.id}:${a.id}`
                      return (
                        <td key={a.id} className={cn('px-3 py-2 text-center',
                          !a.is_active && 'opacity-45')}
                          title={!a.is_active ? 'Аккаунт выключен — линк сохранится, торговля начнётся после активации'
                            : !a.has_keys ? 'Нет API-ключей — линк сохранится, торговля начнётся после их добавления'
                              : undefined}>
                          <Switch on={on} busy={busy}
                            onToggle={() => onToggle(s, a.id, !on)} />
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
              {!strategies.length && !isLoading && (
                <tr><td colSpan={1 + accounts.length}
                  className="px-4 py-8 text-center text-textFaint text-sm">
                  ничего не найдено
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-2xs text-textFaint max-w-[720px]">
        Включение записывает связку в trading_user_strategies и будит матчер.
        Дискавери-стратегиями управляет пайплайн — ручные включения на
        аккаунтах вне пайплайна переживают его прогоны, но сама стратегия
        может быть заархивирована инкумбент-чеком.
      </p>
    </div>
  )
}
